import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
	PlusIcon,
	PencilIcon,
	TrashIcon,
	EyeIcon,
	EyeSlashIcon,
	MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

import { API_URL } from '../../config/api';

// React Quill no es compatible con React 19 (usa findDOMNode que fue removido)
// Deshabilitado temporalmente hasta que haya una versión compatible
// Usamos un textarea mejorado con soporte HTML como alternativa
// Nota: Para un editor WYSIWYG completo en el futuro, considerar alternativas como:
// - TinyMCE (https://www.tiny.cloud/)
// - Froala Editor (https://www.froala.com/wysiwyg-editor)
// - Draft.js (https://draftjs.org/)

const BlogManagement = () => {
	const [posts, setPosts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [searchTerm, setSearchTerm] = useState('');
	const [filterCategory, setFilterCategory] = useState('all');
	const [filterPublished, setFilterPublished] = useState('all');
	const [showModal, setShowModal] = useState(false);
	const [editingPost, setEditingPost] = useState(null);
	const [formData, setFormData] = useState({
		title: '',
		slug: '',
		excerpt: '',
		content: '',
		featured_image: null,
		author: 'PS Asesores & Consultores',
		category: 'general',
		tags: '',
		published: false,
		meta_description: ''
	});
	const [formErrors, setFormErrors] = useState({});
	const [saving, setSaving] = useState(false);
	const [imagePreview, setImagePreview] = useState(null);

	useEffect(() => {
		loadPosts();
	}, []);

	const loadPosts = async () => {
		try {
			setLoading(true);
			const response = await axios.get(`${API_URL}/api/content/blog/`);
			setPosts(Array.isArray(response.data) ? response.data : (response.data.results || []));
			setError('');
		} catch (err) {
			setError('Error al cargar artículos');
			console.error('Error cargando posts:', err);
		} finally {
			setLoading(false);
		}
	};

	const handleCreate = () => {
		setEditingPost(null);
		setFormData({
			title: '',
			slug: '',
			excerpt: '',
			content: '',
			featured_image: null,
			author: 'PS Asesores & Consultores',
			category: 'general',
			tags: '',
			published: false,
			meta_description: ''
		});
		setImagePreview(null);
		setFormErrors({});
		setShowModal(true);
	};

	const handleEdit = (post) => {
		setEditingPost(post);
		
		// React Quill trabaja directamente con HTML, así que usamos el contenido tal cual
		// Si no tiene HTML, lo dejamos como está (React Quill lo manejará)
		let contentForEdit = post.content || '';
		
		setFormData({
			title: post.title || '',
			slug: post.slug || '',
			excerpt: post.excerpt || '',
			content: contentForEdit,
			featured_image: null,
			author: post.author || 'PS Asesores & Consultores',
			category: post.category || 'general',
			tags: post.tags || '',
			published: post.published || false,
			meta_description: post.meta_description || ''
		});
		setImagePreview(post.featured_image_url || null);
		setFormErrors({});
		setShowModal(true);
	};

	const handleSave = async (e) => {
		e.preventDefault();
		setFormErrors({});
		setSaving(true);

		try {
			const formDataToSend = new FormData();
			Object.keys(formData).forEach(key => {
				if (key === 'featured_image') {
					if (formData[key]) {
						formDataToSend.append(key, formData[key]);
					}
				} else if (key === 'content') {
					// Guardar el contenido HTML tal cual, sin modificaciones
					// El usuario escribe HTML directamente en el textarea
					let contentToSave = formData.content || '';
					
					// Solo validar que no esté completamente vacío (solo espacios o saltos de línea)
					const trimmed = contentToSave.trim();
					if (!trimmed) {
						contentToSave = '';
					}
					
					// Guardar el contenido tal cual (puede ser HTML o texto plano)
					// FormData.append() NO escapa el HTML, lo envía tal cual
					formDataToSend.append(key, contentToSave);
				} else if (formData[key] !== null && formData[key] !== undefined) {
					formDataToSend.append(key, formData[key]);
				}
			});

			if (editingPost) {
				await axios.put(`${API_URL}/api/content/blog/${editingPost.slug}/`, formDataToSend, {
					headers: { 'Content-Type': 'multipart/form-data' }
				});
			} else {
				await axios.post(`${API_URL}/api/content/blog/`, formDataToSend, {
					headers: { 'Content-Type': 'multipart/form-data' }
				});
			}

			await loadPosts();
			setShowModal(false);
		} catch (err) {
			if (err.response?.data) {
				setFormErrors(err.response.data);
			} else {
				setError('Error al guardar artículo');
			}
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (slug) => {
		if (!window.confirm('¿Estás seguro de eliminar este artículo?')) return;

		try {
			await axios.delete(`${API_URL}/api/content/blog/${slug}/`);
			await loadPosts();
		} catch (err) {
			setError('Error al eliminar artículo');
		}
	};

	const handleTogglePublished = async (post) => {
		try {
			await axios.patch(`${API_URL}/api/content/blog/${post.slug}/`, {
				published: !post.published
			});
			await loadPosts();
		} catch (err) {
			setError('Error al cambiar estado del artículo');
		}
	};

	const handleImageChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setFormData({ ...formData, featured_image: file });
			setImagePreview(URL.createObjectURL(file));
		}
	};

	const getCategoryLabel = (category) => {
		const labels = {
			contabilidad: 'Contabilidad',
			finanzas: 'Finanzas',
			fiscalidad: 'Fiscalidad',
			administracion: 'Administración',
			constitucion: 'Constitución de Empresas',
			legal: 'Legal',
			consultoria: 'Consultoría',
			general: 'General'
		};
		return labels[category] || category;
	};

	const filteredPosts = posts.filter(post => {
		const matchesSearch = 
			post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
			post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			post.tags?.toLowerCase().includes(searchTerm.toLowerCase());
		const matchesCategory = filterCategory === 'all' || post.category === filterCategory;
		const matchesPublished = 
			filterPublished === 'all' || 
			(filterPublished === 'published' && post.published) ||
			(filterPublished === 'draft' && !post.published);
		return matchesSearch && matchesCategory && matchesPublished;
	});

	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
			{/* Header */}
			<div className="mb-8">
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
					<div>
						<h1 className="text-3xl font-bold text-gray-900">Gestión de Blog</h1>
						<p className="mt-2 text-sm text-gray-600">
							Administra los artículos del blog
						</p>
					</div>
					<button
						onClick={handleCreate}
						className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-opacity"
						style={{ backgroundColor: '#12284C' }}
					>
						<PlusIcon className="h-5 w-5" />
						Crear Artículo
					</button>
				</div>
			</div>

			{/* Filtros */}
			<div className="bg-white rounded-lg shadow p-4 mb-6">
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					{/* Búsqueda */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Buscar
						</label>
						<div className="relative">
							<MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
							<input
								type="text"
								placeholder="Buscar artículos..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
							/>
						</div>
					</div>
					{/* Filtro por categoría */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Categoría
						</label>
						<select
							value={filterCategory}
							onChange={(e) => setFilterCategory(e.target.value)}
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
						>
							<option value="all">Todas</option>
							<option value="contabilidad">Contabilidad</option>
							<option value="finanzas">Finanzas</option>
							<option value="fiscalidad">Fiscalidad</option>
							<option value="administracion">Administración</option>
							<option value="constitucion">Constitución de Empresas</option>
							<option value="legal">Legal</option>
							<option value="consultoria">Consultoría</option>
							<option value="general">General</option>
						</select>
					</div>
					{/* Filtro por estado */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Estado
						</label>
						<select
							value={filterPublished}
							onChange={(e) => setFilterPublished(e.target.value)}
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
						>
							<option value="all">Todos</option>
							<option value="published">Publicados</option>
							<option value="draft">Borradores</option>
						</select>
					</div>
				</div>
			</div>

			{/* Error */}
			{error && (
				<div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
					<p className="text-red-800">{error}</p>
				</div>
			)}

			{/* Lista de Artículos */}
			{loading ? (
				<div className="text-center py-12">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#12284C' }}></div>
					<p className="mt-4 text-gray-600">Cargando artículos...</p>
				</div>
			) : (
				<div className="bg-white rounded-lg shadow overflow-hidden">
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Título
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Categoría
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Autor
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Vistas
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Estado
									</th>
									<th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
										Acciones
									</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{filteredPosts.length === 0 ? (
									<tr>
										<td colSpan="6" className="px-6 py-8 text-center text-gray-500">
											No se encontraron artículos
										</td>
									</tr>
								) : (
									filteredPosts.map((post) => (
										<tr key={post.id} className="hover:bg-gray-50">
											<td className="px-6 py-4">
												<div className="text-sm font-medium text-gray-900">{post.title}</div>
												{post.excerpt && (
													<div className="text-sm text-gray-500 truncate max-w-md">{post.excerpt}</div>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
													{getCategoryLabel(post.category)}
												</span>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="text-sm text-gray-900">{post.author}</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="text-sm text-gray-900">{post.views || 0}</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{post.published ? (
													<span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
														Publicado
													</span>
												) : (
													<span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
														Borrador
													</span>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
												<div className="flex items-center justify-end gap-2">
													<button
														onClick={() => handleTogglePublished(post)}
														className="p-2 hover:bg-gray-100 rounded"
														title={post.published ? 'Despublicar' : 'Publicar'}
													>
														{post.published ? (
															<EyeSlashIcon className="h-5 w-5 text-gray-600" />
														) : (
															<EyeIcon className="h-5 w-5 text-gray-600" />
														)}
													</button>
													<button
														onClick={() => handleEdit(post)}
														className="p-2 hover:bg-gray-100 rounded"
														title="Editar"
													>
														<PencilIcon className="h-5 w-5" style={{ color: '#12284C' }} />
													</button>
													<button
														onClick={() => handleDelete(post.slug)}
														className="p-2 hover:bg-red-50 rounded"
														title="Eliminar"
													>
														<TrashIcon className="h-5 w-5 text-red-600" />
													</button>
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{/* Modal Crear/Editar */}
			{showModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
					<div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
						<div className="p-6">
							<div className="flex items-center justify-between mb-6">
								<h2 className="text-2xl font-bold text-gray-900">
									{editingPost ? 'Editar Artículo' : 'Crear Artículo'}
								</h2>
								<button
									onClick={() => setShowModal(false)}
									className="text-gray-400 hover:text-gray-600"
								>
									<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>

							<form onSubmit={handleSave} className="space-y-4">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Título *
										</label>
										<input
											type="text"
											required
											value={formData.title}
											onChange={(e) => setFormData({ ...formData, title: e.target.value })}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
											style={{ focusRingColor: '#12284C' }}
										/>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Slug (se genera automáticamente si está vacío)
										</label>
										<input
											type="text"
											value={formData.slug}
											onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
											style={{ focusRingColor: '#12284C' }}
										/>
									</div>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Resumen (máximo 300 caracteres) *
									</label>
									<textarea
										required
										maxLength={300}
										value={formData.excerpt}
										onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
										rows={3}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
										style={{ focusRingColor: '#12284C' }}
									/>
									<p className="mt-1 text-xs text-gray-500">{formData.excerpt.length}/300</p>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Contenido * 
										<span className="text-xs text-gray-500 ml-2 font-normal">
											(Usa HTML básico para formato: &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;br&gt;, etc.)
										</span>
									</label>
									<textarea
										required
										value={formData.content || ''}
										onChange={(e) => setFormData({ ...formData, content: e.target.value })}
										rows={20}
										className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y font-mono text-sm"
										placeholder="Escribe el contenido del artículo aquí. Puedes usar HTML básico para formato:

Ejemplos:
&lt;p&gt;Párrafo de texto&lt;/p&gt;
&lt;strong&gt;Texto en negrita&lt;/strong&gt;
&lt;em&gt;Texto en cursiva&lt;/em&gt;
&lt;br&gt; (salto de línea)
&lt;ul&gt;&lt;li&gt;Lista item&lt;/li&gt;&lt;/ul&gt;
&lt;h2&gt;Título&lt;/h2&gt;"
										style={{ minHeight: '400px' }}
									/>
									<div className="mt-2 text-xs text-gray-500 bg-blue-50 p-2 rounded">
										<p><strong>💡 Tip:</strong> El contenido se guardará como HTML. Usa etiquetas HTML básicas para formato.</p>
									</div>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Imagen Destacada {!editingPost ? '(opcional)' : '(dejar vacío para mantener la actual)'}
									</label>
									<input
										type="file"
										accept="image/*"
										onChange={handleImageChange}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg"
									/>
									{imagePreview && (
										<div className="mt-2">
											<img src={imagePreview} alt="Preview" className="max-w-xs h-auto rounded-lg" />
											{editingPost && (
												<p className="text-xs text-gray-500 mt-1">
													{formData.featured_image ? 'Nueva imagen seleccionada' : 'Imagen actual'}
												</p>
											)}
										</div>
									)}
								</div>

								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Autor
										</label>
										<input
											type="text"
											value={formData.author}
											onChange={(e) => setFormData({ ...formData, author: e.target.value })}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
											style={{ focusRingColor: '#12284C' }}
										/>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Categoría *
										</label>
										<select
											required
											value={formData.category}
											onChange={(e) => setFormData({ ...formData, category: e.target.value })}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
											style={{ focusRingColor: '#12284C' }}
										>
											<option value="general">General</option>
											<option value="contabilidad">Contabilidad</option>
											<option value="finanzas">Finanzas</option>
											<option value="fiscalidad">Fiscalidad</option>
											<option value="administracion">Administración</option>
											<option value="constitucion">Constitución de Empresas</option>
											<option value="legal">Legal</option>
											<option value="consultoria">Consultoría</option>
										</select>
									</div>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Etiquetas (separadas por comas)
									</label>
									<input
										type="text"
										value={formData.tags}
										onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
										style={{ focusRingColor: '#12284C' }}
										placeholder="contabilidad, impuestos, empresas"
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Meta Descripción (SEO, máximo 160 caracteres)
									</label>
									<textarea
										maxLength={160}
										value={formData.meta_description}
										onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
										rows={2}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
										style={{ focusRingColor: '#12284C' }}
									/>
									<p className="mt-1 text-xs text-gray-500">{formData.meta_description.length}/160</p>
								</div>

								<div>
									<label className="flex items-center space-x-2">
										<input
											type="checkbox"
											checked={formData.published}
											onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
											className="rounded border-gray-300"
											style={{ accentColor: '#12284C' }}
										/>
										<span className="text-sm font-medium text-gray-700">Publicado</span>
									</label>
								</div>

								{Object.keys(formErrors).length > 0 && (
									<div className="bg-red-50 border border-red-200 rounded-lg p-4">
										{Object.entries(formErrors).map(([key, value]) => (
											<p key={key} className="text-sm text-red-800">
												{key}: {Array.isArray(value) ? value.join(', ') : value}
											</p>
										))}
									</div>
								)}

								<div className="flex justify-end gap-3 pt-4 border-t">
									<button
										type="button"
										onClick={() => setShowModal(false)}
										className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
									>
										Cancelar
									</button>
									<button
										type="submit"
										disabled={saving}
										className="px-4 py-2 rounded-lg text-white transition-opacity disabled:opacity-50"
										style={{ backgroundColor: '#12284C' }}
									>
										{saving ? 'Guardando...' : 'Guardar'}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default BlogManagement;

